/**
 * Recurrence engine for appointment series.
 *
 * Pure calendar arithmetic, shared by the Worker (materializing occurrences)
 * and the browser (previewing "next visits" while the estimator edits a rule).
 *
 * Two layers, kept deliberately separate:
 *
 *   1. Occurrence *dates* are computed as local calendar dates (YYYY-MM-DD
 *      strings) with no timezone involved at all — "every Tuesday" is a fact
 *      about the calendar, not about UTC.
 *   2. `localDateTimeToUtc` then pins one date + wall-clock time in an IANA
 *      zone to an absolute instant. This is the only place DST exists, so a
 *      9am visit stays 9am on the customer's clock all year.
 */

import { SCHEDULE_HORIZON_DAYS, type SeriesFrequency } from '../constants/index.ts';

/**
 * One weekday of a schedule, with the optional differences from the series
 * defaults. Absent time, duration, or crew means "use the schedule's own".
 *
 * `nthWeeks` is monthly only: 1–4 count from the start of the month, 5 means
 * "last". A Tuesday that is both the 4th and the last is produced once.
 */
export interface DaySlot {
  /** 0 = Sunday … 6 = Saturday. */
  dayOfWeek: number;
  /** Monthly nth-weekday mode. Omitted on a weekly slot. */
  nthWeeks?: number[] | null;
  /** Wall-clock 'HH:MM' for this weekday. */
  startTimeLocal?: string | null;
  /** Visit length for this weekday, in minutes. */
  durationMinutes?: number | null;
  /**
   * Crew for this weekday, lead first. `null`/omitted inherits the schedule
   * crew; an empty array is a weekday nobody is assigned to.
   */
  assignedUserIds?: string[] | null;
  /**
   * What a visit on this weekday costs, when it differs from the schedule's
   * price. Belongs to the weekday: two monthly slots on the same weekday
   * (the 1st and 3rd Tuesday) carry the same price.
   */
  priceCents?: number | null;
  /**
   * A checklist template used instead of the schedule's own checklist on
   * this weekday. Rules inside the template still apply.
   */
  checklistTemplateId?: string | null;
}

export interface RecurrenceRule {
  frequency: SeriesFrequency;
  /** 0 = Sunday … 6 = Saturday. Weekly/biweekly and nth-weekday monthly. */
  dayOfWeek?: number | null;
  /**
   * Every selected weekday, when a schedule visits more than one. Preferred
   * over `dayOfWeek` when both are set. `daySlots` wins over both.
   */
  weekdays?: number[] | null;
  /**
   * Repeat every N weeks. Weekly defaults to 1 and biweekly to 2 when this
   * is omitted, so a series saved before the field existed keeps its cadence.
   */
  intervalWeeks?: number | null;
  /** Monthly fixed-date mode; clamped to short months. */
  dayOfMonth?: number | null;
  /** Monthly nth-weekday mode; 1–4, or 5 for "last". */
  nthWeek?: number | null;
  /** Several nths in one month, e.g. the 1st and the 3rd. */
  nthWeeks?: number[] | null;
  /** Per-weekday time and crew. Also the source of the weekdays themselves. */
  daySlots?: DaySlot[] | null;
  /** First candidate date (inclusive) and the week the interval is counted from. */
  startsOn: string;
  /** Last candidate date (inclusive); null/undefined = open-ended. */
  endsOn?: string | null;
}

// ---------------------------------------------------------------------------
// Calendar-date helpers (YYYY-MM-DD, no timezone)
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

function toEpochDay(isoDate: string): number {
  return Date.parse(`${isoDate}T00:00:00Z`) / DAY_MS;
}

function fromEpochDay(epochDay: number): string {
  return new Date(epochDay * DAY_MS).toISOString().slice(0, 10);
}

export function addDaysToDate(isoDate: string, days: number): string {
  return fromEpochDay(toEpochDay(isoDate) + days);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeekOf(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00Z`).getUTCDay();
}

function daysInMonth(year: number, month: number): number {
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function isoDateOf(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

/**
 * "The 2nd Tuesday of March 2026". `nth` 1–4 counts from the start; 5 means
 * the last such weekday, which unlike a literal 5th always exists.
 */
export function nthWeekdayOfMonth(year: number, month: number, dayOfWeek: number, nth: number): string {
  if (nth === 5) {
    const last = daysInMonth(year, month);
    const lastDow = dayOfWeekOf(isoDateOf(year, month, last));
    return isoDateOf(year, month, last - ((lastDow - dayOfWeek + 7) % 7));
  }
  const firstDow = dayOfWeekOf(isoDateOf(year, month, 1));
  return isoDateOf(year, month, 1 + ((dayOfWeek - firstDow + 7) % 7) + (nth - 1) * 7);
}

/** Which occurrence of its weekday a date is, and whether it is also the last. */
export function nthWeekdayIndex(isoDate: string): { nth: number; isLast: boolean } {
  const day = Number(isoDate.slice(8, 10));
  const nth = Math.floor((day - 1) / 7) + 1;
  const [year, month] = isoDate.split('-').map(Number) as [number, number];
  return { nth, isLast: isoDate === nthWeekdayOfMonth(year, month, dayOfWeekOf(isoDate), 5) };
}

function uniqueSorted(days: readonly number[]): number[] {
  return [...new Set(days.filter((day) => day >= 0 && day <= 6))].sort((left, right) => left - right);
}

/** Weekdays a rule visits. Slots win, then an explicit list, then the single day. */
export function weekdaysOfRule(rule: RecurrenceRule): number[] {
  const fromSlots = rule.daySlots?.map((slot) => slot.dayOfWeek) ?? [];
  if (fromSlots.length > 0) return uniqueSorted(fromSlots);
  if (rule.weekdays && rule.weekdays.length > 0) return uniqueSorted(rule.weekdays);
  return [rule.dayOfWeek ?? dayOfWeekOf(rule.startsOn)];
}

/** Every N weeks. An omitted interval keeps the cadence the frequency already named. */
export function weekIntervalOf(rule: RecurrenceRule): number {
  if (rule.intervalWeeks && rule.intervalWeeks > 0) return rule.intervalWeeks;
  return rule.frequency === 'biweekly' ? 2 : 1;
}

function nthWeeksOf(rule: RecurrenceRule, slot?: DaySlot): number[] {
  if (slot?.nthWeeks && slot.nthWeeks.length > 0) return [...new Set(slot.nthWeeks)];
  if (rule.nthWeeks && rule.nthWeeks.length > 0) return [...new Set(rule.nthWeeks)];
  if (rule.nthWeek !== null && rule.nthWeek !== undefined) return [rule.nthWeek];
  return [];
}

/**
 * The slot that owns this occurrence, when the rule names per-day differences.
 * Monthly slots match the nth as well as the weekday, so the 1st Tuesday and
 * the 3rd Tuesday can carry different times.
 */
export function daySlotForDate(rule: RecurrenceRule, isoDate: string): DaySlot | undefined {
  const slots = rule.daySlots?.filter((slot) => slot.dayOfWeek === dayOfWeekOf(isoDate)) ?? [];
  if (slots.length === 0) return undefined;
  if (rule.frequency !== 'monthly') return slots[0];
  const index = nthWeekdayIndex(isoDate);
  return (
    slots.find((slot) => {
      const nths = slot.nthWeeks ?? [];
      if (nths.length === 0) return true;
      return nths.includes(index.nth) || (index.isLast && nths.includes(5));
    }) ?? slots[0]
  );
}

/** Start, length, and crew for one occurrence. Null crew inherits the schedule's. */
export function occurrenceWindow(
  rule: RecurrenceRule,
  isoDate: string,
  fallbackStart: string,
  fallbackDuration: number,
): { startTimeLocal: string; durationMinutes: number; assignedUserIds: string[] | null } {
  const slot = daySlotForDate(rule, isoDate);
  const duration = slot?.durationMinutes;
  return {
    startTimeLocal: slot?.startTimeLocal || fallbackStart,
    durationMinutes: duration && duration >= 15 ? duration : fallbackDuration,
    assignedUserIds: slot?.assignedUserIds ?? null,
  };
}

/**
 * The local times a rule itself writes onto visits.
 *
 * A visit still at one of these was generated. A visit at any other time was
 * moved by hand and must survive a rule edit. Days that share the schedule's
 * own time are one shape; a weekday with its own hour is a shape of its own,
 * and the shared shape excludes those weekdays so a hand-move onto that hour
 * on a different day is not mistaken for a generated visit.
 */
export interface GeneratedTimeShape {
  startTimeLocal: string;
  durationMinutes: number;
  /** Set when this shape belongs to one weekday (0 = Sunday). */
  dayOfWeek: number | null;
  /** Weekdays the shared shape does not cover, because they have their own hour. */
  exceptWeekdays: number[];
}

export function generatedTimeShapes(
  startTimeLocal: string,
  durationMinutes: number,
  daySlots: readonly DaySlot[] | null | undefined,
): GeneratedTimeShape[] {
  const differing = (daySlots ?? []).filter((slot) => {
    const time = slot.startTimeLocal || startTimeLocal;
    const length = slot.durationMinutes && slot.durationMinutes >= 15 ? slot.durationMinutes : durationMinutes;
    return time !== startTimeLocal || length !== durationMinutes;
  });
  if (differing.length === 0) {
    return [{ startTimeLocal, durationMinutes, dayOfWeek: null, exceptWeekdays: [] }];
  }
  const exceptWeekdays = [...new Set(differing.map((slot) => slot.dayOfWeek))].sort((left, right) => left - right);
  return [
    { startTimeLocal, durationMinutes, dayOfWeek: null, exceptWeekdays },
    ...differing.map((slot) => ({
      startTimeLocal: slot.startTimeLocal || startTimeLocal,
      durationMinutes: slot.durationMinutes && slot.durationMinutes >= 15 ? slot.durationMinutes : durationMinutes,
      dayOfWeek: slot.dayOfWeek,
      exceptWeekdays: [] as number[],
    })),
  ];
}

/**
 * Weekly and biweekly dates.
 *
 * The interval is counted in weeks from the week of the first selected
 * weekday on or after `startsOn`. A single Tuesday every two weeks therefore
 * lands on the same dates it did when the rule could name only one day: the
 * anchor is that first Tuesday, and every further Tuesday is N weeks later.
 * Extra weekdays share that week, so Monday/Wednesday/Friday every other week
 * are three days of one week, not three independent fortnights.
 */
function listWeeklyDates(rule: RecurrenceRule, from: string, to: string): string[] {
  const days = weekdaysOfRule(rule);
  const interval = weekIntervalOf(rule);
  const startDow = dayOfWeekOf(rule.startsOn);
  let bestOffset = 7;
  for (const dow of days) {
    const offset = (dow - startDow + 7) % 7;
    if (offset < bestOffset) bestOffset = offset;
  }
  const phaseAnchor = toEpochDay(rule.startsOn) + bestOffset;
  const phaseSunday = phaseAnchor - dayOfWeekOf(fromEpochDay(phaseAnchor));
  const fromDay = toEpochDay(from);
  const toDay = toEpochDay(to);
  const startsDay = toEpochDay(rule.startsOn);

  const firstWeek = Math.max(0, Math.floor((fromDay - phaseSunday) / 7));
  let weekIndex = firstWeek - (firstWeek % interval);
  if (weekIndex < 0) weekIndex = 0;

  const dates: string[] = [];
  for (let week = weekIndex; ; week += interval) {
    const sunday = phaseSunday + week * 7;
    if (sunday > toDay) break;
    for (const dow of days) {
      const day = sunday + dow;
      if (day >= fromDay && day <= toDay && day >= startsDay) dates.push(fromEpochDay(day));
    }
  }
  return dates;
}

function listMonthlyDates(rule: RecurrenceRule, from: string, to: string): string[] {
  const dates: string[] = [];
  let [year, month] = from.split('-').map(Number) as [number, number];
  const [endYear, endMonth] = to.split('-').map(Number) as [number, number];
  const slots =
    rule.daySlots && rule.daySlots.length > 0
      ? rule.daySlots
      : weekdaysOfRule(rule).map((dayOfWeek) => ({ dayOfWeek }) satisfies DaySlot);

  while (year < endYear || (year === endYear && month <= endMonth)) {
    const produced = new Set<string>();
    const nthMode =
      nthWeeksOf(rule).length > 0 || (rule.daySlots ?? []).some((slot) => (slot.nthWeeks?.length ?? 0) > 0);
    const explicitWeekday =
      (rule.daySlots?.length ?? 0) > 0 ||
      (rule.weekdays?.length ?? 0) > 0 ||
      (rule.dayOfWeek !== null && rule.dayOfWeek !== undefined);
    if (rule.dayOfMonth && !nthMode) {
      // Clamp, don't skip: "the 31st" in February means the last day of
      // February — a missing visit surprises the customer more than an early one.
      produced.add(isoDateOf(year, month, Math.min(rule.dayOfMonth, daysInMonth(year, month))));
    } else if (nthMode && explicitWeekday) {
      for (const slot of slots) {
        for (const nth of nthWeeksOf(rule, slot)) {
          produced.add(nthWeekdayOfMonth(year, month, slot.dayOfWeek, nth));
        }
      }
    }
    for (const date of [...produced].sort()) {
      if (date >= from && date <= to) dates.push(date);
    }
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return dates;
}

/**
 * Sensible weekdays for a quote sold at N visits a week.
 *
 * The quote records a count, not named days. One visit stays on whatever day
 * the office picks; two or more open on the pattern cleaners already use
 * (Monday/Thursday, Monday/Wednesday/Friday, weekdays) so one schedule can
 * hold the whole agreement.
 */
export function weekdaysForVisitCount(count: number): number[] {
  const visits = Math.min(7, Math.max(1, Math.trunc(count)));
  const patterns: Record<number, number[]> = {
    1: [1],
    2: [1, 4],
    3: [1, 3, 5],
    4: [1, 2, 4, 5],
    5: [1, 2, 3, 4, 5],
    6: [1, 2, 3, 4, 5, 6],
    7: [0, 1, 2, 3, 4, 5, 6],
  };
  return patterns[visits] ?? [1];
}

/** A single visit cannot run longer than 24 hours. */
export const MAX_VISIT_MINUTES = 1440;

/** Quarter-hour scheduling grid. Zero and anything that is not a number stay zero. */
export function roundUpToQuarterHour(minutes: number): number {
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Math.ceil(minutes / 15) * 15;
}

/**
 * Splits on-site minutes across `dayCount` visits.
 *
 * Each day is a multiple of 15, at least 15, and at most 24 hours. The days
 * sum to the quarter-hour rounding of `totalMinutes`. Returns null when that
 * total cannot be placed: more than seven days, or a day that would have to
 * exceed 24 hours.
 */
export function distributeVisitMinutes(totalMinutes: number, dayCount: number): number[] | null {
  const rounded = roundUpToQuarterHour(totalMinutes);
  const days = Math.trunc(dayCount);
  if (rounded <= 0 || days < 1 || days > 7) return null;
  if (rounded > days * MAX_VISIT_MINUTES) return null;
  if (rounded < days * 15) return null;
  const base = Math.floor(rounded / days / 15) * 15;
  let remainder = rounded - base * days;
  const parts = Array.from({ length: days }, () => base);
  for (let index = 0; index < days && remainder > 0; index += 1) {
    const room = MAX_VISIT_MINUTES - parts[index]!;
    const add = Math.min(room, remainder);
    parts[index] = parts[index]! + add;
    remainder -= add;
  }
  if (remainder !== 0 || parts.some((part) => part < 15 || part > MAX_VISIT_MINUTES)) return null;
  return parts;
}

export interface OnSitePlacement {
  /** Fallback visit length. Never above 24 hours. */
  durationMinutes: number;
  /**
   * Weekdays that together hold the work. Empty when one day is enough, or
   * when the work does not fit in a week of 24-hour days.
   */
  weekdays: number[];
  /** Lengths that differ from `durationMinutes`, keyed by weekday. */
  dayDurations: Array<{ dayOfWeek: number; durationMinutes: number }>;
  /** Quoted minutes that could not be placed. Zero when the placement is complete. */
  unplacedMinutes: number;
}

/**
 * Where a quote's on-site time goes on a schedule.
 *
 * A visit that fits in 24 hours is one day. A longer one is split across the
 * fewest weekdays that can hold it, each at most 24 hours, so saving the
 * schedule does not drop the tail. Work that does not fit in seven such days
 * is reported as unplaced rather than clipped to 24 hours and stored as if
 * that were the whole job.
 */
export function placeOnSiteMinutes(totalMinutes: number): OnSitePlacement {
  const rounded = roundUpToQuarterHour(totalMinutes);
  if (rounded === 0) return { durationMinutes: 0, weekdays: [], dayDurations: [], unplacedMinutes: 0 };
  if (rounded <= MAX_VISIT_MINUTES) {
    return { durationMinutes: rounded, weekdays: [], dayDurations: [], unplacedMinutes: 0 };
  }
  const days = Math.ceil(rounded / MAX_VISIT_MINUTES);
  const parts = days <= 7 ? distributeVisitMinutes(rounded, days) : null;
  if (!parts) {
    return {
      durationMinutes: MAX_VISIT_MINUTES,
      weekdays: [],
      dayDurations: [],
      unplacedMinutes: rounded - MAX_VISIT_MINUTES,
    };
  }
  const weekdays = weekdaysForVisitCount(parts.length);
  const durationMinutes = parts[0]!;
  return {
    durationMinutes,
    weekdays,
    dayDurations: parts.flatMap((duration, index) =>
      duration === durationMinutes ? [] : [{ dayOfWeek: weekdays[index]!, durationMinutes: duration }],
    ),
    unplacedMinutes: 0,
  };
}

function isDaySlot(value: unknown): value is DaySlot {
  if (!value || typeof value !== 'object') return false;
  const day = (value as { dayOfWeek?: unknown }).dayOfWeek;
  return typeof day === 'number' && day >= 0 && day <= 6;
}

/** Parse the JSON column. Anything that is not a list of slots is "no slots". */
export function parseDaySlots(json: string | null | undefined): DaySlot[] | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return null;
    const slots = parsed.filter(isDaySlot).map((slot) => ({
      dayOfWeek: slot.dayOfWeek,
      ...(Array.isArray(slot.nthWeeks) ? { nthWeeks: slot.nthWeeks.filter((nth) => nth >= 1 && nth <= 5) } : {}),
      ...(typeof slot.startTimeLocal === 'string' && slot.startTimeLocal
        ? { startTimeLocal: slot.startTimeLocal }
        : {}),
      ...(typeof slot.durationMinutes === 'number' ? { durationMinutes: slot.durationMinutes } : {}),
      ...(Array.isArray(slot.assignedUserIds)
        ? { assignedUserIds: slot.assignedUserIds.filter((id): id is string => typeof id === 'string') }
        : {}),
      ...(typeof slot.priceCents === 'number' && Number.isSafeInteger(slot.priceCents) && slot.priceCents >= 0
        ? { priceCents: slot.priceCents }
        : {}),
      ...(typeof slot.checklistTemplateId === 'string' && slot.checklistTemplateId
        ? { checklistTemplateId: slot.checklistTemplateId }
        : {}),
    }));
    return slots.length > 0 ? slots : null;
  } catch {
    return null;
  }
}

/** Stable JSON for the column, so a save that changes nothing compares equal. */
export function serializeDaySlots(slots: readonly DaySlot[] | null | undefined): string | null {
  if (!slots || slots.length === 0) return null;
  const normalized = slots
    .filter((slot) => slot.dayOfWeek >= 0 && slot.dayOfWeek <= 6)
    .map((slot) => {
      const nthWeeks = slot.nthWeeks?.filter((nth) => nth >= 1 && nth <= 5);
      return {
        dayOfWeek: slot.dayOfWeek,
        ...(nthWeeks && nthWeeks.length > 0 ? { nthWeeks: [...new Set(nthWeeks)].sort((a, b) => a - b) } : {}),
        ...(slot.startTimeLocal ? { startTimeLocal: slot.startTimeLocal } : {}),
        ...(slot.durationMinutes ? { durationMinutes: slot.durationMinutes } : {}),
        ...(slot.assignedUserIds != null ? { assignedUserIds: [...slot.assignedUserIds] } : {}),
        ...(slot.priceCents != null ? { priceCents: slot.priceCents } : {}),
        ...(slot.checklistTemplateId ? { checklistTemplateId: slot.checklistTemplateId } : {}),
      };
    })
    .sort((left, right) => left.dayOfWeek - right.dayOfWeek || (left.nthWeeks?.[0] ?? 0) - (right.nthWeeks?.[0] ?? 0));
  return normalized.length > 0 ? JSON.stringify(normalized) : null;
}

export interface StoredRuleColumns {
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  nthWeek: number | null;
  intervalWeeks: number | null;
  daySlotsJson: string | null;
}

/**
 * The columns a rule is stored as.
 *
 * The legacy single-day columns stay filled with the first slot so a reader
 * that still asks "which weekday?" gets one, and the JSON holds the whole
 * pattern. A weekly rule with no weekday named stores neither: the engine
 * follows `startsOn`, which is what that omission has always meant.
 */
export function normalizeStoredRule(input: {
  frequency: SeriesFrequency;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  nthWeek?: number | null;
  nthWeeks?: number[] | null;
  intervalWeeks?: number | null;
  weekdays?: number[] | null;
  daySlots?: DaySlot[] | null;
}): StoredRuleColumns {
  const monthlyNth =
    input.frequency === 'monthly' &&
    (input.dayOfMonth === null || input.dayOfMonth === undefined) &&
    (input.nthWeek != null ||
      (input.nthWeeks?.length ?? 0) > 0 ||
      (input.daySlots ?? []).some((slot) => slot.nthWeeks?.length));

  let slots: DaySlot[] = (input.daySlots ?? []).filter((slot) => slot.dayOfWeek >= 0 && slot.dayOfWeek <= 6);
  if (slots.length === 0 && input.weekdays && input.weekdays.length > 0 && input.frequency !== 'one_time') {
    slots = input.weekdays.map((dayOfWeek) => ({ dayOfWeek }));
  }
  if (
    slots.length === 0 &&
    input.dayOfWeek != null &&
    input.frequency !== 'one_time' &&
    input.frequency !== 'monthly'
  ) {
    slots = [{ dayOfWeek: input.dayOfWeek }];
  }
  if (slots.length === 0 && input.dayOfWeek != null && monthlyNth) {
    slots = [{ dayOfWeek: input.dayOfWeek }];
  }
  if (input.frequency === 'monthly' && input.dayOfMonth != null && !monthlyNth) {
    slots = [];
  }
  if (monthlyNth) {
    const shared = input.nthWeeks?.length ? input.nthWeeks : input.nthWeek != null ? [input.nthWeek] : undefined;
    slots = slots.map((slot) => ({
      ...slot,
      nthWeeks: slot.nthWeeks?.length ? slot.nthWeeks : shared,
    }));
  } else {
    slots = slots.map((slot) => ({ ...slot, nthWeeks: undefined }));
  }

  const interval =
    input.frequency === 'weekly' || input.frequency === 'biweekly'
      ? input.intervalWeeks && input.intervalWeeks > 0
        ? input.intervalWeeks
        : input.frequency === 'biweekly'
          ? 2
          : 1
      : null;
  const json = serializeDaySlots(slots);
  const first = parseDaySlots(json)?.[0];
  return {
    dayOfWeek: first?.dayOfWeek ?? null,
    dayOfMonth: input.frequency === 'monthly' && !monthlyNth ? (input.dayOfMonth ?? null) : null,
    nthWeek: first?.nthWeeks?.[0] ?? null,
    intervalWeeks: interval,
    daySlotsJson: json,
  };
}

/**
 * How many visits a monthly total is divided across, expressed as visits per
 * week so the existing `total × 12 / (52 × visitsPerWeek)` arithmetic still
 * applies.
 *
 * A plain weekly rule with one weekday does not answer: the quote's own
 * `visitsPerWeek` does, which is what that formula used before a schedule
 * could name several days. A pattern that does name them — three weekdays,
 * every two weeks, the 1st and the 3rd Tuesday — replaces it.
 */
export function effectiveWeeklyVisits(input: {
  frequency: SeriesFrequency;
  visitsPerWeek?: number | null;
  weekdayCount: number;
  intervalWeeks: number;
  monthlyOccurrences: number;
}): number {
  const quoted = Math.max(1, input.visitsPerWeek ?? 1);
  if (input.frequency === 'weekly' || input.frequency === 'biweekly') {
    const defaultInterval = input.frequency === 'biweekly' ? 2 : 1;
    const days = Math.max(1, input.weekdayCount);
    const interval = Math.max(1, input.intervalWeeks);
    if (days === 1 && interval === defaultInterval) return quoted;
    return days / interval;
  }
  if (input.frequency === 'monthly') {
    const occurrences = Math.max(1, input.monthlyOccurrences);
    if (occurrences === 1) return quoted;
    return (occurrences * 12) / 52;
  }
  return quoted;
}

/** Counts the form and the server both price from, read off one rule. */
export function cadenceOfRule(
  rule: RecurrenceRule,
  visitsPerWeek?: number | null,
): {
  visitsPerWeek: number | null | undefined;
  weekdayCount: number;
  intervalWeeks: number;
  monthlyOccurrences: number;
} {
  const weekdays = weekdaysOfRule(rule);
  const explicitDays = (rule.daySlots?.length ?? 0) > 0 || (rule.weekdays?.length ?? 0) > 0 || rule.dayOfWeek != null;
  let monthlyOccurrences = 1;
  if (rule.frequency === 'monthly' && !rule.dayOfMonth) {
    // Count the dates a full year actually produces. Selecting the 4th and
    // the last weekday lists two weeks, but in a month with only four of
    // that weekday they are the same day and the engine visits it once.
    // Dividing by the checkbox count would bill that month at half.
    const probe: RecurrenceRule = { ...rule, startsOn: '2026-01-01', endsOn: null };
    const dates = listOccurrenceDates(probe, '2026-01-01', '2026-12-31');
    monthlyOccurrences = dates.length / 12;
    if (!(monthlyOccurrences > 0)) {
      const slots = rule.daySlots?.filter((slot) => (slot.nthWeeks?.length ?? 0) > 0) ?? [];
      monthlyOccurrences =
        slots.length > 0
          ? slots.reduce((sum, slot) => sum + (slot.nthWeeks?.length ?? 0), 0)
          : nthWeeksOf(rule).length * Math.max(1, explicitDays ? weekdays.length : 1);
    }
  }
  return {
    visitsPerWeek,
    weekdayCount: explicitDays ? weekdays.length : 1,
    intervalWeeks: weekIntervalOf(rule),
    monthlyOccurrences: Math.max(1, monthlyOccurrences),
  };
}

// ---------------------------------------------------------------------------
// Occurrence dates
// ---------------------------------------------------------------------------

/**
 * All dates the rule produces within [windowStart, windowEnd] (inclusive).
 *
 * The window is how the caller expresses "the next 12 weeks" — the rule's own
 * startsOn/endsOn bounds are applied on top of it. Returned in ascending order.
 */
export function listOccurrenceDates(rule: RecurrenceRule, windowStart: string, windowEnd: string): string[] {
  const from = rule.startsOn > windowStart ? rule.startsOn : windowStart;
  const to = rule.endsOn && rule.endsOn < windowEnd ? rule.endsOn : windowEnd;
  if (from > to) return [];

  switch (rule.frequency) {
    case 'one_time':
      return rule.startsOn >= from && rule.startsOn <= to ? [rule.startsOn] : [];

    case 'weekly':
    case 'biweekly':
      return listWeeklyDates(rule, from, to);

    case 'monthly':
      return listMonthlyDates(rule, from, to);
  }
}

/**
 * Which stored slots a PATCH should keep.
 *
 * A caller that sends `daySlots` or `weekdays` is naming the pattern. A caller
 * that still sends only `dayOfWeek` or `nthWeek` — the fields from before a
 * series could hold several days — is replacing that pattern, so the slots
 * already stored must not win and silently discard the change. A notes-level
 * patch that mentions neither leaves the slots alone.
 */
export function storedSlotsForPatch(patch: object, existing: DaySlot[] | null): DaySlot[] | null {
  if ('daySlots' in patch) return (patch as { daySlots?: DaySlot[] | null }).daySlots ?? null;
  if ('weekdays' in patch) return null;
  if ('dayOfWeek' in patch || 'nthWeek' in patch || 'nthWeeks' in patch || 'dayOfMonth' in patch) return null;
  return existing;
}

/**
 * How many appointment rows one series create is about to write.
 *
 * The public demo charges this against the shared calendar cap. A weekly
 * series with no days named is one visit a week, which is what that cap
 * assumed; Monday/Wednesday/Friday is three, and every N weeks divides it.
 */
export function estimatedHorizonAppointments(input: {
  frequency?: unknown;
  intervalWeeks?: unknown;
  weekdays?: unknown;
  dayOfWeek?: unknown;
  nthWeeks?: unknown;
  daySlots?: unknown;
}): number {
  const frequency = typeof input.frequency === 'string' ? input.frequency : '';
  if (frequency === 'one_time') return 1;
  const slots = Array.isArray(input.daySlots) ? input.daySlots : null;
  const weekdays = Array.isArray(input.weekdays) ? input.weekdays.filter((day) => typeof day === 'number') : null;
  const interval = Math.max(
    1,
    typeof input.intervalWeeks === 'number' && input.intervalWeeks > 0
      ? input.intervalWeeks
      : frequency === 'biweekly'
        ? 2
        : 1,
  );
  if (frequency === 'monthly') {
    let perMonth = 1;
    if (slots && slots.length > 0) {
      perMonth = slots.reduce((sum, slot) => {
        const nthWeeks = (slot as { nthWeeks?: unknown }).nthWeeks;
        const count = Array.isArray(nthWeeks) ? nthWeeks.length : 1;
        return sum + Math.max(1, count);
      }, 0);
    } else if (Array.isArray(input.nthWeeks) && input.nthWeeks.length > 0) {
      const days = weekdays && weekdays.length > 0 ? new Set(weekdays).size : 1;
      perMonth = input.nthWeeks.length * days;
    }
    return Math.max(1, Math.ceil(perMonth * (SCHEDULE_HORIZON_DAYS / 30)));
  }
  let perInterval = 1;
  if (slots && slots.length > 0) {
    perInterval = new Set(slots.map((slot) => (slot as { dayOfWeek?: unknown }).dayOfWeek)).size;
  } else if (weekdays && weekdays.length > 0) {
    perInterval = new Set(weekdays).size;
  } else if (frequency === 'daily') {
    perInterval = 7;
  }
  return Math.max(1, Math.ceil((perInterval / interval) * (SCHEDULE_HORIZON_DAYS / 7)));
}

/** Two of a schedule's own visits that would occupy the same hour for one crew. */
export function firstSelfOverlap<T extends { startsAt: string; endsAt: string }>(
  slots: readonly T[],
): { earlier: T; later: T } | null {
  const ordered = [...slots].sort((left, right) =>
    left.startsAt < right.startsAt ? -1 : left.startsAt > right.startsAt ? 1 : 0,
  );
  let previous = ordered[0];
  for (let index = 1; index < ordered.length; index += 1) {
    const current = ordered[index];
    if (!previous || !current) continue;
    if (current.startsAt < previous.endsAt) return { earlier: previous, later: current };
    if (current.endsAt > previous.endsAt) previous = current;
  }
  return null;
}

/**
 * Whether two crews cannot both be on the clock at once.
 *
 * The same person on both crews is one person in two places. An empty crew is
 * nobody named, which holds the hour for the whole location, so it blocks
 * every other crew and is blocked by them. Disjoint crews do not block each
 * other: that is two jobs at the same hour, which a company with two crews
 * does on purpose.
 */
export function crewsBlockEachOther(left: readonly string[], right: readonly string[]): boolean {
  if (left.length === 0 || right.length === 0) return true;
  const members = new Set(right);
  return left.some((id) => members.has(id));
}

/**
 * Two of a schedule's own visits that would clash, including when their crews
 * only partly overlap or one of them is unassigned.
 *
 * Grouping by the exact crew list misses `[A, B]` against `[B]` and an
 * unassigned visit against a named one. The database guard treats both as
 * conflicts, so a preflight that does not will create the series and then
 * silently drop the later visit.
 */
export function firstCrewOverlap<T extends { startsAt: string; endsAt: string; crew: readonly string[] }>(
  slots: readonly T[],
): { earlier: T; later: T } | null {
  const ordered = [...slots].sort((left, right) =>
    left.startsAt < right.startsAt ? -1 : left.startsAt > right.startsAt ? 1 : 0,
  );
  const active: T[] = [];
  for (const current of ordered) {
    const still: T[] = [];
    for (const earlier of active) {
      if (earlier.endsAt <= current.startsAt) continue;
      still.push(earlier);
      if (crewsBlockEachOther(earlier.crew, current.crew)) return { earlier, later: current };
    }
    still.push(current);
    active.splice(0, active.length, ...still);
  }
  return null;
}

/** A short preview for the series form: the first `count` dates from `fromDate`. */
export function previewOccurrenceDates(rule: RecurrenceRule, fromDate: string, count: number): string[] {
  // Monthly is the sparsest ordinary cadence, about one visit a month, so a
  // year covers a short preview. An every-N-weeks rule is sparser than that
  // once N is large: four visits of an every-52-weeks schedule need four
  // years, plus one interval so a start that falls a full interval after
  // `fromDate` still leaves `count` visits in the window.
  const interval =
    rule.frequency === 'weekly' || rule.frequency === 'biweekly'
      ? Math.max(1, rule.intervalWeeks ?? (rule.frequency === 'biweekly' ? 2 : 1))
      : 1;
  const windowDays = Math.max(366, count * 31, interval * 7 * (count + 1));
  return listOccurrenceDates(rule, fromDate, addDaysToDate(fromDate, windowDays)).slice(0, count);
}

// ---------------------------------------------------------------------------
// Local wall-clock → UTC instant
// ---------------------------------------------------------------------------

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timezone: string): Intl.DateTimeFormat {
  let dtf = dtfCache.get(timezone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    dtfCache.set(timezone, dtf);
  }
  return dtf;
}

/** The wall-clock a given instant shows in `timezone`, as a UTC epoch of those digits. */
function wallClockAsUtc(timezone: string, epochMs: number): number {
  const parts: Record<string, number> = {};
  for (const part of formatterFor(timezone).formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return Date.UTC(
    parts.year ?? 1970,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  );
}

/**
 * A zone's UTC offset in minutes at a given instant — positive east of
 * Greenwich, so New York reads -240 in August and -300 in January.
 *
 * The instant is floored to the second first: `wallClockAsUtc` formats to
 * second precision, so a millisecond remainder would otherwise land in the
 * subtraction and turn a whole-minute offset into a fraction of one.
 *
 * An unrecognized zone answers 0 rather than throwing, the same forgiveness
 * `localDateTimeToUtc` shows — callers that need to *know* the zone is real
 * (writing a VTIMEZONE, say) have to ask separately.
 */
export function zoneOffsetMinutes(timezone: string, epochMs: number): number {
  const atSecond = Math.floor(epochMs / 1000) * 1000;
  try {
    return (wallClockAsUtc(timezone, atSecond) - atSecond) / 60_000;
  } catch {
    return 0;
  }
}

/**
 * Pins a local date + 'HH:MM' wall-clock time in an IANA zone to a UTC instant,
 * returned as an ISO string.
 *
 * Two-pass fixed-point search: guess the instant as if the wall clock were
 * UTC, measure the zone's offset at that guess, correct, and measure once more
 * so a guess that landed the wrong side of a DST transition is repaired. A
 * wall time skipped by spring-forward (2:30am on the gap day) resolves to the
 * instant after the jump, which is when a crew would actually arrive.
 *
 * An unrecognized timezone falls back to UTC rather than failing — the same
 * forgiveness `todayIsoDateIn` shows, because a bad zone string must not stop
 * the schedule rendering.
 */
/**
 * Does this wall clock exist on this date, in this zone?
 *
 * False only inside the spring-forward gap — the hour the clocks jump over,
 * where 02:30 is a time nobody's watch ever shows. `localDateTimeToUtc`
 * deliberately answers with the instant the clock has jumped PAST, which is
 * right for a visit already booked at that wall time: it is when the crew
 * actually arrives. It is wrong for OFFERING one, because the offer would be
 * labelled with an hour that does not happen and stored as another.
 */
export function localWallTimeExists(isoDate: string, timeHHMM: string, timezone: string): boolean {
  const instant = localDateTimeToUtc(isoDate, timeHHMM, timezone);
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(instant));
    const at = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
    // `en-CA` renders midnight as 24; the ISO wall clock calls it 00.
    const hour = at('hour') === '24' ? '00' : at('hour');
    return `${at('year')}-${at('month')}-${at('day')}` === isoDate && `${hour}:${at('minute')}` === timeHHMM;
  } catch {
    // An unknown zone cannot be checked, and refusing every hour in it would
    // be worse than the gap this guards against.
    return true;
  }
}

export function localDateTimeToUtc(isoDate: string, timeHHMM: string, timezone: string): string {
  const [hourRaw, minuteRaw] = timeHHMM.split(':');
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  const asUtc = Date.UTC(year, month - 1, day, Number(hourRaw), Number(minuteRaw));

  try {
    const first = asUtc - (wallClockAsUtc(timezone, asUtc) - asUtc);
    const second = asUtc - (wallClockAsUtc(timezone, first) - first);
    if (wallClockAsUtc(timezone, second) === asUtc) return new Date(second).toISOString();
    // No instant shows this wall clock — it sits inside the spring-forward
    // gap. The two passes bracket the transition; the later one is the moment
    // the clock has jumped past the requested time, which is when a crew
    // scheduled for 2:30am would actually arrive.
    return new Date(Math.max(first, second)).toISOString();
  } catch {
    return new Date(asUtc).toISOString();
  }
}

/** Minutes after an instant, as an ISO string. */
export function addMinutesToInstant(isoInstant: string, minutes: number): string {
  return new Date(Date.parse(isoInstant) + minutes * 60_000).toISOString();
}
